import qs from "qs";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SearchBox from "../../common/searchBox";
import Table from "../../common/table";
import {
	useGetAllGifts,
	useGetAllGiftStatus,
	useGetAllPaymentStatus,
} from "../../../queryHooks";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import CS from "../../../services/commonService";
import getBadgeClasses from "../../../util/badgeClass";
import Icon from "../../icons/index";
import ReactTooltip from "react-tooltip";
import { URL } from "../../../constants/URL";
import { useStore } from "../../../zustand/store";
import { modules } from "../../../constants/Modules";
import RenderUser from "../../common/User";

//	Pre-defined values
const columnKey = "pickUpOrders";

//	Component starts
const PickUpOrdersTable = ({ restaurantId, tabChangeCounter }) => {
	const navigate = useNavigate();
	const lastPageData = useStore((state) => state.lastPageData);
	const setLastPageData = useStore((state) => state.setLastPageData);

	//	State values
	const [giftStatusList, setGiftStatusList] = useState();
	const [paymentStatusList, setPaymentStatusList] = useState();
	const [tableData, setTableData] = useState();
	const [pageNow, setPageNow] = useState(
		lastPageData?.[`${modules.PICK_UP_ORDERS}${restaurantId}`]?.page || 1
	);
	const [itemsPerPage, setItemsPerPage] = useState(
		lastPageData?.[`${modules.PICK_UP_ORDERS}${restaurantId}`]
			?.items_per_page || 10
	);
	const [giftsPayload, setGiftsPayload] = useState(
		lastPageData?.[`${modules.PICK_UP_ORDERS}${restaurantId}`] || {
			pagination: true,
			items_per_page: itemsPerPage,
			page: pageNow,
			filter: {
				["restaurant_id"]: restaurantId,
				["gift_type"]: ["ASAP_PICK_UP", "SCHEDULED_PICK_UP"],
			},
			sort: { created_at: "DESC" },
		}
	);

	const { data: dataGetAllGifts, isLoading } = useGetAllGifts(
		qs.stringify(giftsPayload)
	);

	useEffect(() => {
		if (dataGetAllGifts && dataGetAllGifts.status === "success") {
			let respData = dataGetAllGifts.data;
			respData.page = pageNow;
			respData.items_per_page = itemsPerPage;
			setTableData(respData);
		}
	}, [dataGetAllGifts]);

	const { data: dataGetGiftStatus } = useGetAllGiftStatus();

	useEffect(() => {
		if (dataGetGiftStatus && dataGetGiftStatus.status === "success") {
			let _statuses = [];
			dataGetGiftStatus.data.forEach((element) => {
				_statuses.push({
					label: element.label || element.value,
					value: element.id,
				});
			});
			setGiftStatusList(_statuses);
		}
	}, [dataGetGiftStatus]);

	const { data: dataGetPaymentStatus } = useGetAllPaymentStatus();

	useEffect(() => {
		if (dataGetPaymentStatus && dataGetPaymentStatus.status === "success") {
			let _statuses = [];
			dataGetPaymentStatus.data.forEach((element) => {
				_statuses.push({
					label: element.label || element.value,
					value: element.id,
				});
			});
			setPaymentStatusList(_statuses);
		}
	}, [dataGetPaymentStatus]);

	useEffect(() => {
		setGiftsPayload({
			...giftsPayload,
			filter: {
				...giftsPayload.filter,
				["restaurant_id"]: restaurantId,
				["gift_type"]: ["ASAP_PICK_UP", "SCHEDULED_PICK_UP"],
			},
		});
	}, [tabChangeCounter]);

	useEffect(() => {
		setPageNow(1);
		setItemsPerPage(10);
		setGiftsPayload({
			pagination: true,
			items_per_page: 10,
			page: 1,
			filter: {
				["restaurant_id"]: restaurantId,
				["gift_type"]: ["ASAP_PICK_UP", "SCHEDULED_PICK_UP"],
			},
			sort: { created_at: "DESC" },
		});
	}, [restaurantId]);

	useEffect(() => {
		setLastPageData({
			...lastPageData,
			[`${modules.PICK_UP_ORDERS}${restaurantId}`]: giftsPayload,
		});
	}, [giftsPayload]);

	const columns = [
		{
			label: "Order Info",
			type: "string",
			sort: true,
			path: "gift_id",
			alias: "data_gifts.gift_id",
			content: (item) => (
				<>
					<Link
						to={URL.giftOrderDetails.replace(":slug", item.id)}
						className="table-user-name"
					>
						{item.gift_id || item.id.substring(0, 8)}
					</Link>
				</>
			),
		},
		{
			label: "User Info",
			type: "string",
			sort: true,
			path: "recipient_name",
			alias: "recipient_name",
			content: (item) => {
				const recipient = item?.recipient;
				if (!recipient) return "-";

				return (
					<div className="d-flex align-items-center">
						<div className="user-item mr-2">
							{recipient.profile_image_url ? (
								<img
									src={recipient.profile_image_url}
									alt="User"
									className="rounded-circle"
									style={{ width: "32px", height: "32px", objectFit: "cover" }}
								/>
							) : (
								<div
									className="rounded-circle bg-secondary d-flex align-items-center justify-content-center"
									style={{
										width: "32px",
										height: "32px",
										color: "white",
										fontSize: "12px",
									}}
								>
									{(recipient.first_name?.[0] || "U").toUpperCase()}
								</div>
							)}
						</div>
						<div>
							<div style={{ fontSize: "14px", fontWeight: "500" }}>
								{`${recipient.first_name || ""} ${
									recipient.last_name || ""
								}`.trim() ||
									recipient.username ||
									"Unknown User"}
							</div>
						</div>
					</div>
				);
			},
		},
		{
			label: "Order Type",
			type: "string",
			sort: true,
			path: "gift_type",
			alias: "data_gifts.gift_type",
			aliasFilter: "data_gifts.gift_type",
			filterOptions: [
				{ label: "ASAP", value: "ASAP_PICK_UP" },
				{ label: "Scheduled", value: "SCHEDULED_PICK_UP" },
			],
			content: (item) => {
				const isASAP = item.gift_type === "ASAP_PICK_UP";
				return (
					<span
						className={`badge ${isASAP ? "badge-warning" : "badge-primary"}`}
						style={{
							fontSize: "11px",
							fontWeight: "600",
							padding: "6px 12px",
							borderRadius: "20px",
							textTransform: "uppercase",
							letterSpacing: "0.5px",
						}}
					>
						{isASAP ? "ASAP" : "SCHEDULED"}
					</span>
				);
			},
		},
		{
			label: "Status",
			type: "string",
			sort: true,
			path: "status_value",
			alias: "gift_statuses.id",
			aliasFilter: "data_gifts.status",
			filterOptions: giftStatusList,
			content: (item) => (
				<span
					className={`badge ${getBadgeClasses(
						item.status?.id || item.status,
						"gift"
					)}`}
				>
					{item.status?.value || item.status_label}
				</span>
			),
		},
		{
			label: "Payment Status",
			type: "string",
			sort: true,
			path: "payment_status_value",
			alias: "payment_statuses.id",
			aliasFilter: "data_gifts.payment_status",
			filterOptions: paymentStatusList,
			content: (item) => (
				<span
					className={`badge ${getBadgeClasses(
						item.payment_status?.id || item.payment_status,
						"payment"
					)}`}
				>
					{item.payment_status?.value || item.payment_status_label}
				</span>
			),
		},
		{
			label: "Pick Up Date",
			type: "date",
			sort: true,
			path: "scheduled_for",
			alias: "data_gifts.scheduled_for",
			content: (item) =>
				item.scheduled_for ? getCurrentTimezoneDate(item.scheduled_for) : "-",
		},
		{
			label: "Created Date",
			type: "date",
			sort: true,
			path: "created_at",
			alias: "data_gifts.created_at",
			content: (item) => getCurrentTimezoneDate(item.created_at),
		},
		{
			label: "Partner Order ID",
			type: "string",
			sort: true,
			path: "partner_order_id",
			alias: "data_gifts.partner_order_id",
			content: (item) => item.partner_order_id || "-",
		},
		{
			label: "Action",
			type: "string",
			sort: false,
			path: "action",
			content: (item) => (
				<>
					<Link
						to={URL.giftOrderDetails.replace(":slug", item.id)}
						state={{ source: "pickup" }}
						className="btn btn-success-icon-only btn-sm me-1"
						data-tip
						data-for="show"
					>
						<Icon type="show" />
					</Link>
					<ReactTooltip
						id="show"
						place="top"
						type="dark"
						effect="solid"
						delayHide={1000}
					>
						<span>View Details</span>
					</ReactTooltip>
				</>
			),
		},
	];

	return (
		<>
			<div className="dataTables_wrapper">
				<div className="content-area position-relative">
					<div className="grid">
						<div className="grid-content">
							<div className="user-listing-filterOptions mb-2 mt-3 d-block">
								<div className="row mb-2">
									<div className="col-sm-8 mb-2 position-static">
										<div className="left-buttons d-flex">
											<SearchBox
												columnKey={columnKey}
												search={(value) => {
													setPageNow(1);
													setGiftsPayload({
														...giftsPayload,
														search: {
															"data_gifts.gift_id":
																!value && value === "" ? undefined : value,
															recipient_name:
																!value && value === "" ? undefined : value,
														},
														page: 1,
													});
												}}
											/>
										</div>
									</div>
								</div>
							</div>
							<Table
								columnKey={columnKey}
								columns={columns}
								tableData={tableData}
								sortCol={(obj) => {
									setPageNow(1);
									setGiftsPayload({
										...giftsPayload,
										sort: {
											[obj.alias]:
												!obj.sort && obj.sort === "" ? undefined : obj.sort,
										},
										page: 1,
									});
								}}
								setCurrentPage={(page) => {
									setPageNow(page);
									setGiftsPayload({ ...giftsPayload, page });
								}}
								setCurrentLimit={(items_per_page) => {
									setPageNow(1);
									setItemsPerPage(items_per_page);
									setGiftsPayload({
										...giftsPayload,
										items_per_page,
										page: 1,
									});
								}}
								loading={isLoading}
								filterValue={async (obj) => {
									if (obj.filter === null) {
										const resp = await CS.filterData(giftsPayload.filter, obj);
										setPageNow(1);
										setGiftsPayload({
											...giftsPayload,
											filter: resp,
											page: 1,
										});
									} else {
										setPageNow(1);
										let newFilter = { ...giftsPayload.filter };

										// Special handling for different filter types
										if (obj.alias === "data_gifts.gift_type") {
											// When filtering by gift_type, replace the entire gift_type filter
											newFilter["gift_type"] = obj.filter;
										} else if (obj.alias === "data_gifts.status") {
											// Status filter
											newFilter["status"] = obj.filter;
										} else if (obj.alias === "data_gifts.payment_status") {
											// Payment status filter
											newFilter["payment_status"] = obj.filter;
										} else {
											newFilter[obj.alias] = obj.filter;
										}

										setGiftsPayload({
											...giftsPayload,
											filter: newFilter,
											page: 1,
										});
									}
								}}
								filterDate={(dates) => {
									setPageNow(1);
									setGiftsPayload({
										...giftsPayload,
										filter_date: dates && {
											...giftsPayload?.filter_date,
											[dates.alias]: String(dates.filter),
										},
										page: 1,
									});
								}}
								tabChangeCounter={tabChangeCounter}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default PickUpOrdersTable;
